import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Share2, HandCoins } from "lucide-react";
import { SubHeader } from "@/components/Brand";
import { useApi } from "@/lib/api";
import { rp } from "@/lib/format";

const C = { navy: "#0f172a", muted: "#475569", line: "#cbd5e1" };
const fmtWib = (s) => new Date(s).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " WIB";

const Row = ({ label, value, sub, testid }) => (
  <div className="flex justify-between items-start gap-4 py-2.5">
    <span style={{ color: C.muted }} className="text-[14px]">{label}</span>
    <div className="text-right">
      <div style={{ color: C.navy }} className="font-bold text-[15px]" data-testid={testid}>{value}</div>
      {sub && <div style={{ color: C.muted }} className="text-[12.5px]">{sub}</div>}
    </div>
  </div>
);

const Card = ({ l, cardRef }) => {
  const d = l.disbursement;
  return (
    <div ref={cardRef} className="bg-white keep-white mx-auto w-full max-w-[380px] rounded-2xl px-6 pt-7 pb-7 shadow-lg" style={{ backgroundColor: "#fff" }} data-testid="disbursement-receipt">
      <div className="flex justify-center"><img src={`${process.env.PUBLIC_URL}/brand-banner.webp`} alt="CU Tuah Talino" className="h-[70px] w-auto object-contain" /></div>
      <div className="flex justify-center mt-5"><span className="h-14 w-14 rounded-full flex items-center justify-center" style={{ backgroundColor: "#16a34a" }}><HandCoins className="h-7 w-7 text-white" /></span></div>
      <h1 style={{ color: C.navy }} className="text-center text-[24px] font-extrabold mt-3">Pencairan Berhasil</h1>
      <p style={{ color: C.muted }} className="text-center text-[13.5px]" data-testid="disb-receipt-no">No. {d.receipt_no}</p>
      <p style={{ color: C.muted }} className="text-center text-[13.5px]">{fmtWib(d.at)}</p>
      <div className="my-4 border-t-2 border-dashed" style={{ borderColor: C.line }} />
      <Row label="Nama Anggota" value={l.member_name} sub={`No. Anggota ${l.member_no}`} />
      <Row label="Metode Pencairan" value={d.method_label} sub={d.method === "bank" ? `${d.bank_name} • ${d.bank_account} a.n. ${d.bank_holder}` : null} testid="disb-receipt-method" />
      <Row label="Tenor / Bunga" value={`${l.tenor_months} bulan • ${l.rate}%/bln`} />
      <Row label="Angsuran per Bulan" value={rp(l.monthly_installment)} />
      <Row label="Petugas" value={d.by_name} sub={d.by_position} />
      <div className="flex justify-between items-center rounded-lg px-4 py-3.5 -mx-2 mt-1" style={{ backgroundColor: "#dcfce7" }}>
        <span style={{ color: C.navy }} className="text-[16px] font-semibold">Dana Dicairkan</span>
        <span style={{ color: C.navy }} className="text-[20px] font-extrabold" data-testid="disb-receipt-amount">{rp(d.amount)}</span>
      </div>
      <div className="my-5 border-t-2 border-dashed" style={{ borderColor: C.line }} />
      <p style={{ color: C.muted }} className="text-center text-[13px] leading-snug">Simpan bukti ini sebagai tanda terima dana pinjaman<br />CU TUAH TALINO.</p>
    </div>
  );
};

export default function DisbursementReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useApi("/loans");
  const l = (data || []).find((x) => x.loan_id === id && x.disbursement);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const name = l ? `bukti-pencairan-${l.disbursement.receipt_no}.png` : "";
  const image = async () => (await fetch(await toPng(ref.current, { pixelRatio: 3, cacheBust: true }))).blob();

  const download = async () => {
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await image());
      a.download = name;
      a.click();
    } catch { toast.error("Gagal membuat gambar bukti pencairan"); } finally { setBusy(false); }
  };
  const share = async () => {
    setBusy(true);
    try {
      const file = new File([await image()], name, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: "Bukti Pencairan Pinjaman CU Tuah Talino" });
      else toast.info("Perangkat tidak mendukung berbagi langsung, gunakan Unduh Bukti");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Gagal membagikan bukti pencairan"); } finally { setBusy(false); }
  };

  return (
    <div className="pb-8 min-h-full bg-slate-100 keep-bg">
      <SubHeader title="Bukti Pencairan" onBack={() => navigate("/app/pinjaman")} />
      <div className="px-5 pt-2">
        {!l ? <p className="text-center text-sm text-slate-500 py-10" data-testid="disb-receipt-state">{loading || !data ? "Memuat..." : "Bukti pencairan tidak ditemukan."}</p> : <Card l={l} cardRef={ref} />}
        {l && (
          <div className="grid grid-cols-2 gap-3 mt-6 max-w-[380px] mx-auto">
            <button onClick={download} disabled={busy} data-testid="disb-receipt-download" className="flex items-center justify-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white font-semibold py-3 rounded-xl"><Download className="h-4 w-4" />Unduh Bukti</button>
            <button onClick={share} disabled={busy} data-testid="disb-receipt-share" className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl"><Share2 className="h-4 w-4" />Bagikan</button>
          </div>
        )}
      </div>
    </div>
  );
}
